import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  capacityTickCount,
  capacityTierDescription,
  capacityTierLabel,
} from '../utils/capacityGuidance';
import './CapacityMark.css';

const POPOVER_EST_HEIGHT = 64;

/**
 * Quiet 4-tick mark with hover/tap popover explaining cup role.
 * Places the placard below when there isn't room above (avoids panel crop).
 */
function CapacityMark({ tier, interactive = true }) {
  const filled = capacityTickCount(tier);
  const label = capacityTierLabel(tier);
  const description = capacityTierDescription(tier);
  const tipId = useId();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('above');

  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceAbove < POPOVER_EST_HEIGHT + 12 && spaceBelow >= spaceAbove) {
      setPlacement('below');
    } else {
      setPlacement('above');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const ticks = (
    <span className="capacity-mark__ticks" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={`capacity-mark__tick${i < filled ? ' capacity-mark__tick--on' : ''}`}
        />
      ))}
    </span>
  );

  if (!interactive) {
    return (
      <span
        className={`capacity-mark capacity-mark--${tier || 'low'}`}
        aria-label={`${label}. ${description}`}
      >
        {ticks}
      </span>
    );
  }

  return (
    <span
      ref={rootRef}
      className={
        `capacity-mark capacity-mark--${tier || 'low'}`
        + (open ? ' capacity-mark--open' : '')
        + ` capacity-mark--${placement}`
      }
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="capacity-mark__trigger"
        aria-label={`${label}. ${description}`}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        {ticks}
      </button>
      {open && (
        <span
          className={`capacity-mark__popover capacity-mark__popover--${placement}`}
          id={tipId}
          role="tooltip"
        >
          <span className="capacity-mark__popover-title">{label}</span>
          <span className="capacity-mark__popover-body">{description}</span>
        </span>
      )}
    </span>
  );
}

export default CapacityMark;
